import { useState, useEffect, useCallback } from 'react'
import { showToast } from '@/components/layout/Toast'
import EditorFlujo, { type Flujo } from './EditorFlujo'
import ChatDePrueba from './ChatDePrueba'

/**
 * El editor de flujo de un agente, con su borrador y su chat de prueba.
 *
 * ─── Por qué se separó ───
 *
 * Vivía dentro de `RecepcionistaConfig` hasta que apareció el bot de soporte. Lo que los
 * dos comparten es ESTO: el ciclo borrador → probar → publicar. Lo que no comparten son
 * sus textos y su tabla de sesiones, que siguen en la pantalla de cada uno.
 *
 * Copiar el bloque habría significado que el próximo arreglo del ciclo de publicación se
 * hiciera en un solo lado — y el otro bot, el que le habla a los clientes que ya tenemos,
 * sería el que se quedara con el bug.
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface Props {
  slug: string
  /** Qué hace este flujo, en una línea, arriba del editor. */
  descripcion: React.ReactNode
  /** El primer mensaje del chat de prueba es el reporte, y se lee antes de arrancar. */
  leerTodo?: boolean
}

export default function FlujoDelAgente({ slug, descripcion, leerTodo }: Props) {
  // `publicado` es lo que corre; `enEdicion` es lo que se está tocando. Son dos cosas
  // distintas a propósito: el bot sigue con lo publicado hasta que alguien pulse Publicar.
  const [publicado, setPublicado] = useState<Flujo | null>(null)
  const [enEdicion, setEnEdicion] = useState<Flujo | null>(null)
  const [guardadoBorrador, setGuardadoBorrador] = useState<Flujo | null>(null)
  const [rotos, setRotos] = useState<string[]>([])
  const [sueltos, setSueltos] = useState<string[]>([])
  const [guardando, setGuardando] = useState(false)
  const [cargando, setCargando] = useState(true)

  const token = () => localStorage.getItem('auth_token')

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const res = await fetch(`${API_URL}/api/agentes/${slug}/flujo`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      if (res.ok) {
        const f = await res.json()
        setPublicado(f.flujo ?? null)
        setGuardadoBorrador(f.borrador ?? null)
        // Se arranca editando el borrador si existe: es lo que hace que cerrar la
        // pantalla y volver no pierda el trabajo a medias.
        setEnEdicion(f.borrador ?? f.flujo ?? null)
      }
    } catch {
      showToast('No se pudo cargar el flujo', 'error')
    } finally {
      setCargando(false)
    }
  }, [slug])

  useEffect(() => { void cargar() }, [cargar])

  /** Revisa el flujo en vivo contra el servidor: las reglas viven allá, no en el formulario. */
  const revisar = useCallback(async (f: Flujo) => {
    try {
      const res = await fetch(`${API_URL}/api/agentes/${slug}/revisar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ flujo: f }),
      })
      const d = await res.json()
      setRotos(d.valido ? (d.rotos ?? []) : (d.errores ?? []))
      setSueltos(d.huerfanos ?? [])
    } catch {
      /* la revisión es ayuda, no un portón: si falla, guardar vuelve a validar en serio */
    }
  }, [slug])

  const alCambiar = (f: Flujo) => {
    setEnEdicion(f)
    void revisar(f)
  }

  const guardarFlujo = async (publicar: boolean) => {
    if (!enEdicion) return
    setGuardando(true)
    try {
      const res = await fetch(`${API_URL}/api/agentes/${slug}/flujo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ flujo: enEdicion, publicar }),
      })
      const d = await res.json()
      if (!res.ok) { showToast(d.errores?.[0] ?? d.error ?? 'No se pudo guardar', 'error'); return }

      showToast(d.estado, 'success')
      if (publicar) { setPublicado(enEdicion); setGuardadoBorrador(null) }
      else setGuardadoBorrador(enEdicion)
    } catch {
      showToast('No se pudo guardar', 'error')
    } finally {
      setGuardando(false)
    }
  }

  const descartarBorrador = async () => {
    try {
      await fetch(`${API_URL}/api/agentes/${slug}/borrador`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
      })
      setGuardadoBorrador(null)
      setEnEdicion(publicado)
      setRotos([]); setSueltos([])
      showToast('Borrador descartado', 'success')
    } catch {
      showToast('No se pudo descartar', 'error')
    }
  }

  // «Sucio» es lo que está en pantalla contra lo último guardado. Es lo que decide si el
  // chat de prueba avisa que está corriendo otra cosa.
  const guardadoActual = guardadoBorrador ?? publicado
  const haySinGuardar = Boolean(enEdicion) && JSON.stringify(enEdicion) !== JSON.stringify(guardadoActual)

  if (cargando) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Cargando el flujo…</div>
      </div>
    )
  }

  if (!enEdicion) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h2>La conversación</h2></div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
          Este agente todavía no tiene un flujo publicado.
        </p>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header"><h2>La conversación</h2></div>

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 16px' }}>
        {descripcion}
      </p>

      {guardadoBorrador && (
        <div className="editor-flujo__aviso editor-flujo__aviso--info">
          <div>
            Hay un borrador guardado que el bot <strong>todavía no atiende</strong>.
            Probalo en el chat de acá abajo, y publicalo cuando esté.
          </div>
          <button className="btn btn-sm btn-secondary" onClick={() => void descartarBorrador()} type="button">
            Descartar
          </button>
        </div>
      )}

      <div className="editor-flujo__layout">
        <EditorFlujo flujo={enEdicion} onCambio={alCambiar} rotos={rotos} huerfanos={sueltos} />
        <ChatDePrueba
          slug={slug}
          borrador={Boolean(guardadoBorrador)}
          haySinGuardar={haySinGuardar}
          leerTodo={leerTodo}
        />
      </div>

      <div className="editor-flujo__acciones">
        <button
          className="btn btn-secondary"
          onClick={() => void guardarFlujo(false)}
          disabled={guardando || rotos.length > 0 || !haySinGuardar}
          type="button"
        >
          {guardando ? 'Guardando…' : 'Guardar borrador'}
        </button>

        {/* Publicar sólo aparece cuando hay algo pendiente de verdad, para que no se
            pulse por costumbre: es el único botón que le habla a un cliente. */}
        {(guardadoBorrador || haySinGuardar) && (
          <button
            className="btn btn-primary"
            onClick={() => void guardarFlujo(true)}
            disabled={guardando || rotos.length > 0}
            type="button"
          >
            Publicar
          </button>
        )}
      </div>
    </div>
  )
}
